import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { useParams } from 'react-router-dom';
import uniqid from 'uniqid';
import Case from 'case';
import { Box, Text } from 'grommet';
import {
  Seo,
  AppButton,
  SubmissionLoader,
  Spinning,
  SimpleModal,
} from '@Components/Control';
import { useAppTheme } from '@Components/Context';
import { AdminNavWrapper } from '@Components/Navigation';
import { fileImportsConfig } from '@Components/configs';
import {
  ProductFormSectionWrapper,
  ProductFormFieldContainer,
  ProductFormSingleTextInput,
  ProductFormMediaUpload,
  ProductFormDropdownSelect,
  ProductFormHybridInputSelect,
  ProductFormCheckBoxInput,
  ProductFormTimezoneSelect,
} from '@Components/Partial/Product/NewSearch';
import {
  fetchCompanyListRequest,
  clearCompanyListRequest,
  clearFileImportResultRequest,
} from '@Actions';
import ImportResultSummary from './ImportResultSummary';

/* eslint-disable camelcase */

const pickOptionDefaultValue = (field) => {
  if (field.defaultValue !== undefined) {
    return field.defaultValue;
  }

  if (Array.isArray(field.options) && field.options.length > 0) {
    const firstOption = field.options[0];

    if (typeof firstOption === 'string') {
      return firstOption;
    }

    if (firstOption?.value !== undefined) {
      return firstOption.value;
    }

    if (firstOption?.api !== undefined) {
      return firstOption.api;
    }
  }

  return null;
};

const getFieldDefaultValue = (field) => {
  switch (field.type) {
    case 'checkbox':
      return field.defaultValue ?? false;
    case 'radioSelect':
    case 'timezone':
    case 'select':
      return pickOptionDefaultValue(field);
    default:
      return field.defaultValue ?? '';
  }
};

const buildSelectOptions = (field) => {
  if (!Array.isArray(field.options)) return null;

  return field.options.map((opt) => {
    if (typeof opt === 'string') {
      return { api: opt, display: field.keepLabel ? opt : Case.capital(opt) };
    }

    if (opt?.api !== undefined && opt?.display !== undefined) {
      return opt;
    }

    if (opt?.value !== undefined) {
      return { api: opt.value, display: opt.label ?? opt.value };
    }

    return { api: opt, display: opt };
  });
};

const buildInitialFieldValues = (config) => ({
  name: '',
  company_id: '',
  file: null,
  ...(Array.isArray(config?.extraFields)
    ? config.extraFields.reduce((acc, field) => {
      if (field?.key) {
        acc[field.key] = getFieldDefaultValue(field);
      }
      return acc;
    }, {})
    : {}),
});

/*
 * This page is used to handle logic for all file import forms in the admin panel.
 * It provides shared functionality such as form state management, validation,
 * CSV template generation, and layout structure. Individual import pages can
 * pass in specific configurations and additional form fields by customizing
 * the fileImportsConfig.js file in components/configs.
 */
const NewFileImportPage = ({
  small,
  location,
  loading,
  submitLoading,
  fetchCompanyList,
  clearCompanyList,
  clearFileImportResult,
  companyList = null,
  importResult = null,
  ...boundActions
}) => {
  const { id } = useParams();
  const [activeImportKey, setActiveImportKey] = React.useState(fileImportsConfig[0].key);
  const activeConfig = React.useMemo(
    () => fileImportsConfig.find((cfg) => cfg.key === activeImportKey) || fileImportsConfig[0],
    [activeImportKey, fileImportsConfig],
  );
  const initialFieldValues = React.useMemo(
    () => buildInitialFieldValues(activeConfig),
    [activeConfig],
  );
  const [formValues, setFormValues] = React.useState({
    fieldValues: initialFieldValues,
    resetKey: uniqid(),
  });
  // Validate required fields (including extraFields)
  const [submitEnabled, setSubmitEnabled] = React.useState(false);
  const { theme: { page, button, navigation } } = useAppTheme();

  React.useEffect(() => {
    fetchCompanyList();

    return () => {
      clearCompanyList();
    };
  }, []);

  React.useEffect(() => {
    const found = id ? fileImportsConfig.find((cfg) => cfg.key === id) : null;
    if (found && activeImportKey !== found.key) {
      setActiveImportKey(found.key);
      setFormValues({
        fieldValues: buildInitialFieldValues(found),
        resetKey: uniqid(),
      });
    } else if (!id && activeImportKey !== fileImportsConfig[0].key) {
      setActiveImportKey(fileImportsConfig[0].key);
      setFormValues({
        fieldValues: buildInitialFieldValues(fileImportsConfig[0]),
        resetKey: uniqid(),
      });
    }
  }, [id, fileImportsConfig]);

  React.useEffect(() => {
    const requiredKeys = [
      ...activeConfig.requiredInputKeys,
      ...(activeConfig.extraFields?.filter((f) => f.required).map((f) => f.key) || []),
    ];
    const allRequiredFilled = requiredKeys.every((key) => {
      const value = formValues.fieldValues[key];
      if (value === undefined || value === null) return false;
      if (typeof value === 'string') return value.trim().length > 0;
      if (value instanceof File) return true;
      if (Array.isArray(value)) return value.length > 0;
      return !!value;
    });
    setSubmitEnabled(allRequiredFilled);
  }, [formValues, activeConfig]);

  const handleFormValues = (value, key) => {
    setFormValues((prev) => ({
      ...prev,
      fieldValues: {
        ...prev.fieldValues,
        [key]: value,
      },
    }));
  };

  const handleFormReset = () => {
    setFormValues({
      fieldValues: buildInitialFieldValues(activeConfig),
      resetKey: uniqid(),
    });
  };

  // Submit using the bound action for the current import type
  const handleFormSubmit = () => {
    if (boundActions[activeConfig.key]) {
      boundActions[activeConfig.key](formValues.fieldValues, handleFormReset);
    }
  };

  const requiredLabel = (label, key) => {
    const requiredKeys = [
      ...activeConfig.requiredInputKeys,
      ...(activeConfig.extraFields?.filter((f) => f.required).map((f) => f.key) || []),
    ];
    return requiredKeys.includes(key) ? `${label}*` : label;
  };

  // Dropdown for import type selection
  const dropdownOptions = fileImportsConfig.map((cfg) => cfg.label);
  const handleImportTypeChange = (selectedLabel) => {
    const selectedConfig = fileImportsConfig.find((cfg) => cfg.label === selectedLabel);
    if (!selectedConfig) return;
    setActiveImportKey(selectedConfig.key);
    setFormValues({
      fieldValues: buildInitialFieldValues(selectedConfig),
      resetKey: uniqid(),
    });
  };
  /* eslint-enable camelcase */

  const selectedCategory = (
    formValues.fieldValues?.category
    || activeConfig?.csvDefaultCategory
    || null
  );
  const activeCsvConfig = React.useMemo(() => {
    if (activeConfig?.csvConfigByCategory) {
      if (selectedCategory && activeConfig.csvConfigByCategory[selectedCategory]) {
        return activeConfig.csvConfigByCategory[selectedCategory];
      }
      if (
        activeConfig.csvDefaultCategory
        && activeConfig.csvConfigByCategory[activeConfig.csvDefaultCategory]
      ) {
        return activeConfig.csvConfigByCategory[activeConfig.csvDefaultCategory];
      }
      return activeConfig.csvConfigByCategory.default || [];
    }
    return activeConfig?.csvConfig || [];
  }, [activeConfig, selectedCategory]);

  // Render extra fields if present
  // more should be added here as needed, check the DynamicFormRenderer component
  // to see other field type examples
  const renderExtraFields = () => {
    if (!activeConfig?.extraFields?.length) return null;

    const addFields = activeConfig.extraFields.map((field) => {
      if (field.type === 'radioSelect') {
        const fieldValue = formValues?.fieldValues[field.key];
        let dropdownLabel = field.placeholder ? field.placeholder : `Select ${field.label}`;
        const overrideOptions = buildSelectOptions(field);

        if (overrideOptions) {
          const matched = fieldValue
            ? overrideOptions.find(({ api }) => api === fieldValue)
            : null;

          if (matched) {
            dropdownLabel = matched.display;
          } else if (fieldValue) {
            dropdownLabel = field.keepLabel ? fieldValue : Case.capital(fieldValue);
          }
        } else if (fieldValue) {
          dropdownLabel = field.keepLabel ? fieldValue : Case.capital(fieldValue);
        }

        return (
          <ProductFormFieldContainer
            key={field.key}
            small={small}
            fieldTitle={requiredLabel(field.label, field.key)}
            description={field.description}
            primaryText={page.primaryText}
            hintText={page.hintText}
            borderColor={navigation.border}
          >
            <ProductFormDropdownSelect
              selectable
              autoSelect
              label={dropdownLabel}
              value={fieldValue}
              keepLabel={field.keepLabel}
              searchable={field.searchable}
              handleFormValues={(val) => handleFormValues(val, field.key)}
              inputConfig={overrideOptions ? { overrideOptions } : undefined}
              simpleOptions={!overrideOptions ? field.options : undefined}
            />
          </ProductFormFieldContainer>
        );
      }

      if (field.type === 'timezone') {
        const fieldValue = formValues?.fieldValues[field.key];
        return (
          <ProductFormFieldContainer
            key={field.key}
            small={small}
            fieldTitle={requiredLabel(field.label, field.key)}
            primaryText={page.primaryText}
            hintText={page.hintText}
            borderColor={navigation.border}
          >
            <ProductFormTimezoneSelect
              small={small}
              value={fieldValue}
              handleFormValues={(val) => handleFormValues(val, field.key)}
            />
          </ProductFormFieldContainer>
        );
      }

      if (field.type === 'singleTextInput') {
        return (
          <ProductFormFieldContainer
            key={field.key}
            small={small}
            fieldTitle={requiredLabel(field.label, field.key)}
            description={field.description}
            primaryText={page.primaryText}
            hintText={page.hintText}
            borderColor={navigation.border}
          >
            <ProductFormSingleTextInput
              key={formValues.resetKey}
              focusHighlight={page.focusHighlight}
              small={small}
              value={formValues?.fieldValues[field.key] || ''}
              handleFormValues={(val) => handleFormValues(val, field.key)}
            />
          </ProductFormFieldContainer>
        );
      }

      if (field.type === 'checkbox') {
        return (
          <ProductFormFieldContainer
            key={field.key}
            small={small}
            fieldTitle={requiredLabel(field.label, field.key)}
            description={field.description}
            primaryText={page.primaryText}
            hintText={page.hintText}
            borderColor={navigation.border}
          >
            <ProductFormCheckBoxInput
              key={formValues.resetKey}
              borderColor={page.inputBorder}
              focusHighlight={page.focusHighlight}
              checkmarkColor={page.highlight}
              value={formValues?.fieldValues[field.key] || false}
              label={field.label}
              handleFormValues={(val) => handleFormValues(val, field.key)}
            />
          </ProductFormFieldContainer>
        );
      }

      return null;
    });

    return addFields;
  };

  const handleDownloadExampleCSV = () => {
    const headerColumns = activeCsvConfig?.map(({ name }) => name) || [];
    const sampleRow = activeCsvConfig?.map(({ sample }) => (sample !== undefined ? sample : '')) || [];
    const csvLines = [headerColumns.join(','), sampleRow.join(',')].join('\n');

    const importKey = Case.snake(activeConfig?.label);
    let categoryLabel = selectedCategory;
    const categoryField = activeConfig?.extraFields?.find((field) => field.key === 'category');

    if (categoryField && Array.isArray(categoryField.options)) {
      const matchedOption = categoryField.options.find((option) => {
        if (typeof option === 'string') return option === selectedCategory;
        if (option?.value !== undefined) return option.value === selectedCategory;
        if (option?.api !== undefined) return option.api === selectedCategory;
        return false;
      });

      if (matchedOption) {
        if (typeof matchedOption === 'string') {
          categoryLabel = matchedOption;
        } else {
          categoryLabel = matchedOption.display
            || matchedOption.label
            || matchedOption.value
            || matchedOption.api;
        }
      }
    }

    const categoryKey = categoryLabel ? `_${Case.snake(categoryLabel)}` : '';

    const blob = new Blob([`${csvLines}\n`], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = `file_import_template_${importKey}${categoryKey}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);

    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const fieldsLabel = `${activeConfig?.label} Fields`;
  const requiredHeaders = activeCsvConfig?.filter(({ required }) => required) || [];
  const optionalHeaders = activeCsvConfig?.filter(({ required }) => !required) || [];
  const submitMessage = `Uploading ${activeConfig?.label} File. This may take a few minutes depending on the file size.`;
  const formatCompanies = companyList?.map(({ id: api, name }) => ({ api, display: name })) || [];

  const renderFormContent = () => {
    if (loading || !companyList?.length > 0) {
      return (
        <Box flex align="center" justify="center" background={page.background}>
          <Spinning size="large" color={page.highlight} />
        </Box>
      );
    }

    return (
      <Box
        flex
        direction={small ? 'column' : 'row'}
        gap="2rem"
        justify={small ? 'start' : 'evenly'}
        pad={small ? '1.5rem' : { horizontal: '1.5rem', vertical: '2rem' }}
        width="100%"
      >
        <Box gap="2rem" width="100%" direction="column" style={!small ? { flex: 1, minWidth: 0 } : {}}>
          {/* Required shared fields */}
          <ProductFormSectionWrapper
            small={small}
            sectionBg={page.altBg}
            sectionTitle={fieldsLabel}
            titleColor={page.primaryText}
            borderColor={navigation.border}
          >
            <ProductFormFieldContainer
              small={small}
              fieldTitle="Import Type"
              description="Required fields for each import type are marked with *"
              primaryText={page.primaryText}
              hintText={page.hintText}
              borderColor={navigation.border}
            >
              <ProductFormDropdownSelect
                selectable
                autoSelect={!id}
                label={activeConfig?.label}
                handleFormValues={(importType) => handleImportTypeChange(importType)}
                simpleOptions={dropdownOptions}
              />
            </ProductFormFieldContainer>
            <ProductFormFieldContainer
              small={small}
              fieldTitle={requiredLabel('Import Name', 'name')}
              primaryText={page.primaryText}
              hintText={page.hintText}
              borderColor={navigation.border}
            >
              <ProductFormSingleTextInput
                key={formValues.resetKey}
                focusHighlight={page.focusHighlight}
                placeholder="Enter a unique name for this import"
                small={small}
                value={formValues.fieldValues.name || ''}
                handleFormValues={(val) => handleFormValues(val, 'name')}
              />
            </ProductFormFieldContainer>
            <ProductFormFieldContainer
              small={small}
              fieldTitle={requiredLabel('Company ID', 'company_id')}
              primaryText={page.primaryText}
              hintText={page.hintText}
              borderColor={navigation.border}
            >
              <ProductFormHybridInputSelect
                searchable
                key={formValues.resetKey}
                inputProps={{
                  placeholder: 'Enter an ID or select from dropdown',
                }}
                dropdownProps={{
                  inputConfig: {
                    overrideOptions: formatCompanies,
                  },
                }}
                small={small}
                value={formValues.fieldValues.company_id || ''}
                handleFormValues={(val) => handleFormValues(val, 'company_id')}
              />
            </ProductFormFieldContainer>
            {/* Additional fields from configs if present */}
            {renderExtraFields()}
            {/* CSV File Upload final input */}
            <ProductFormFieldContainer
              small={small}
              fieldTitle={requiredLabel('CSV File', 'file')}
              primaryText={page.primaryText}
              hintText={page.hintText}
              borderColor={navigation.border}
            >
              <ProductFormMediaUpload
                singleFile
                key={formValues.resetKey}
                inputConfig={{
                  acceptedFileType: ['.csv'],
                }}
                small={small}
                maxInputs={1}
                handleFormValues={(val) => handleFormValues(val, 'file')}
              />
            </ProductFormFieldContainer>
          </ProductFormSectionWrapper>
          <Box direction="row" gap="1rem" justify="start" pad={{ left: '2rem' }}>
            <AppButton
              width="6.5rem"
              onClick={() => handleFormReset()}
              level="dynamicLarge"
              color={button.primaryBg}
              fontWeight={600}
              label="Cancel"
            />
            <AppButton
              overrideHover
              disabled={!submitEnabled}
              onClick={() => (submitEnabled ? handleFormSubmit() : null)}
              level="dynamicLarge"
              color={button.primaryText}
              bgColor={button.primaryBg}
              fontWeight={600}
              label="Submit"
            />
          </Box>
        </Box>
        <Box gap="2rem" direction="column" style={!small ? { flex: 1, minWidth: 0 } : {}}>
          <ProductFormSectionWrapper
            small={small}
            sectionBg={page.altBg}
            sectionTitle="CSV Columns"
            titleColor={page.primaryText}
            borderColor={navigation.border}
          >
            <ProductFormFieldContainer
              small={small}
              primaryText={page.primaryText}
              hintText={page.hintText}
              borderColor={navigation.border}
            >
              <Box gap="0.5rem" width="100%" direction="column">
                <Box pad={{ bottom: '0.25rem' }} border={{ side: 'bottom', color: navigation.border }}>
                  <Text size="1rem" weight={700} color={page.primaryText}>
                    Required
                  </Text>
                </Box>
                {requiredHeaders.map(({ name, description }) => (
                  <Box key={name} direction="row">
                    <Text size="0.9rem" color={page.primaryText} weight={600}>
                      {name}
                      {description && (
                        <>
                          :&nbsp;
                          <Text size="0.875rem" weight={400}>{description}</Text>
                        </>
                      )}
                    </Text>
                  </Box>
                ))}
                <Box gap="0.5rem" width="100%" direction="column" pad={{ top: '1rem' }}>
                  <Box pad={{ bottom: '0.25rem' }} border={{ side: 'bottom', color: navigation.border }}>
                    <Text size="1rem" weight={700} color={page.primaryText} pad={{ top: '1rem' }}>
                      Optional
                    </Text>
                  </Box>
                  {optionalHeaders?.length > 0 ? optionalHeaders.map(({ name, description }) => (
                    <Box key={name} direction="row">
                      <Text size="0.9rem" color={page.primaryText} weight={600}>
                        {name}
                        {description && (
                          <>
                            :&nbsp;
                            <Text size="0.875rem" weight={400}>{description}</Text>
                          </>
                        )}
                      </Text>
                    </Box>
                  )) : (
                    <Text size="0.9rem" color={page.primaryText} weight={400}>
                      No optional fields for this import type.
                    </Text>
                  )}
                </Box>
                <Box pad={{ top: '0.5rem' }}>
                  <AppButton
                    overrideHover
                    onClick={() => handleDownloadExampleCSV()}
                    level="dynamicLarge"
                    width="14rem"
                    color={button.primaryText}
                    bgColor={button.primaryBg}
                    fontWeight={600}
                    label="Download CSV Template"
                  />
                </Box>
              </Box>
            </ProductFormFieldContainer>
          </ProductFormSectionWrapper>
        </Box>
      </Box>
    );
  };

  return (
    <AdminNavWrapper
      small={small}
      location={location}
      bannerProps={{
        title: activeConfig?.label,
        subTitle: 'File Imports',
      }}
    >
      <Seo />
      {submitLoading && (
        <SubmissionLoader
          textColor={page.modalText}
          loaderColor={page.highlight}
          message={submitMessage}
          contentWidth="20rem"
        />
      )}
      {importResult && (
        <SimpleModal
          noClickOutside
          small={small}
          modalTitle={importResult.success ? 'Import Successful' : 'Import Completed with Errors'}
          toggleModal={() => clearFileImportResult()}
          customHeaderBg={importResult.success ? 'var(--app-success)' : 'var(--app-warning)'}
          textColor="white"
          highlightColor={page.highlight}
          titleSize="1.5rem"
        >
          <ImportResultSummary
            importResult={importResult}
            textColor={page.primaryText}
            clearImportResult={() => clearFileImportResult()}
          />
        </SimpleModal>
      )}
      {renderFormContent()}
    </AdminNavWrapper>
  );
};

function mapStateToProps(state) {
  return {
    loading: state.fetchLoader.secondaryDataLoading,
    submitLoading: state.fetchLoader.submissionLoading,
    companyList: state.adminCompany.companies,
    importResult: state.adminFileImports.fileImportResult,
  };
}

function mapDispatchToProps(dispatch) {
  // Collect all unique actions from config
  const actions = {
    fetchCompanyList: fetchCompanyListRequest,
    clearCompanyList: clearCompanyListRequest,
    clearFileImportResult: clearFileImportResultRequest,
  };

  fileImportsConfig.forEach((cfg) => {
    if (cfg.submitAction && typeof cfg.submitAction === 'function') {
      actions[cfg.key] = cfg.submitAction;
    }
  });
  // Bind all actions
  return bindActionCreators(actions, dispatch);
}

NewFileImportPage.propTypes = {
  small: PropTypes.bool.isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
    search: PropTypes.string.isRequired,
    key: PropTypes.string.isRequired,
  }).isRequired,
  loading: PropTypes.bool.isRequired,
  submitLoading: PropTypes.bool.isRequired,
  fetchCompanyList: PropTypes.func.isRequired,
  clearCompanyList: PropTypes.func.isRequired,
  clearFileImportResult: PropTypes.func.isRequired,
  companyList: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
  })),
  importResult: PropTypes.shape({
    success: PropTypes.bool.isRequired,
    fileImportId: PropTypes.string,
    fileImportName: PropTypes.string,
    summary: PropTypes.object,
    errors: PropTypes.arrayOf(PropTypes.any),
  }),
};

export default connect(mapStateToProps, mapDispatchToProps)(NewFileImportPage);
